import React, { useEffect, useState } from "react";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  AvatarGroup,
  IconButton,
  Stack,
  Divider,
  useMediaQuery,
} from "@mui/material";
import {
  Cancel,
  Visibility,
  CalendarToday,
  Euro,
  ShoppingBag,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { FaRupeeSign } from "react-icons/fa";


const Orders = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detect screen <600px

  let _id = secureLocalStorage.getItem("n_id");
  let token = secureLocalStorage.getItem("n_token");

  const [orderlist, setOrderList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Pending");
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    orderId: null,
  });

  const fetchOrders = async () => {
    try {
      let data = {
        user_id: _id,
        order_status: selectedStatus,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API}website/my_order_status`,
        data,
        { headers: { token } }
      );
      setOrderList(res.data.data || []);
    } catch (err) {
      console.log(err);
      setOrderList([]);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [selectedStatus]);

  const cancelOrder = async (orderid) => {
    try {
      let data = {
        order_id: orderid,
        user_id: _id,
        order_status: "Cancel",
        region: "yyyy",
      };
      await axios.post(`${process.env.REACT_APP_API}website/cancel_order`, data, {
        headers: { token },
      });
      fetchOrders(); // Refresh list after cancel
    } catch (error) {
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Cancel":
        return "error";
      case "Pending":
        return "warning";
      case "Completed":
        return "success";
      default:
        return "primary";
    }
  };

  const getStatusVariant = (status) =>
    status === "Cancel" ? "outlined" : "filled";

  const viewOrder = (items) => {
    secureLocalStorage.setItem("orderid", items.order_id);
    Navigate(`/track_order`);
  };

  const handleCancelClick = (orderId) =>
    setCancelDialog({ open: true, orderId });

  const handleCancelConfirm = () => {
    if (cancelDialog.orderId) cancelOrder(cancelDialog.orderId);
    setCancelDialog({ open: false, orderId: null });
  };

  const handleCancelClose = () =>
    setCancelDialog({ open: false, orderId: null });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: { xs: 2, sm: 4 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{ fontWeight: 600, mb: { xs: 1, sm: 0 } }}
        >
          Orders
        </Typography>

        {/* Filter Buttons */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          {["Pending", "In Progress", "Cancel", "Completed"].map((status) => (
            <Button
              key={status}
              variant={selectedStatus === status ? "contained" : "outlined"}
              size={isMobile ? "small" : "medium"}
              onClick={() => setSelectedStatus(status)}
              sx={{ borderRadius: 2 }}
            >
              {status}
            </Button>
          ))}
        </Box>
      </Box>

      {/* No Orders */}
      {orderlist?.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: { xs: 4, sm: 8 },
            color: "text.secondary",
          }}
        >
          <ShoppingBag sx={{ fontSize: { xs: 48, sm: 64 }, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" gutterBottom>
            No orders yet
          </Typography>
          <Typography variant="body2">
            Your orders will appear here once you make a purchase
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {orderlist.map((items) => (
            <Grid item xs={12} sm={6} md={4} key={items.order_id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: { sm: "translateY(-4px)" },
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 0,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header */}
                  <Box sx={{ p: 2, pb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        label={items.order_status}
                        color={getStatusColor(items.order_status)}
                        variant={getStatusVariant(items.order_status)}
                        size="small"
                      />
                      {items.order_status !== "Cancel" &&
                        items.order_status !== "Completed" && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelClick(items.order_id)}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        )}
                    </Box>
                  </Box>

                  <Divider />

                  {/* Details */}
                  <Box sx={{ p: 2, flex: 1 }}>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CalendarToday
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Order date:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ ml: 1, fontWeight: 500 }}
                        >
                          {items.order_date}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FaRupeeSign
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Total:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            color: "primary.main",
                          }}
                        >
                          ₹{items.grand_total}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Products + View */}
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <AvatarGroup
                      max={isMobile ? 3 : 4}
                      sx={{
                        "& .MuiAvatar-root": {
                          width: 32,
                          height: 32,
                        },
                      }}
                    >
                      {items?.products?.map((product, index) => (
                        <Avatar
                          key={index}
                          src={`${process.env.REACT_APP_API}uploads/${product.product_image}`}
                          alt="Product"
                          sx={{ border: "2px solid white" }}
                        />
                      ))}
                    </AvatarGroup>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => viewOrder(items)}
                      sx={{
                        minWidth: "auto",
                        px: 2,
                        borderRadius: 2,
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialog.open}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span">
            Cancel Order
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to cancel this order? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCancelClose} color="inherit">
            Keep Order
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
